import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { motion } from 'framer-motion';
import { Edit3, ExternalLink, FileText, HelpCircle, Loader, Trash, Upload } from 'lucide-react';
import { useState } from 'react';
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { Link } from 'react-router';
import * as XLSX from 'xlsx';
import { z } from 'zod/v3';

import { loraApi } from '@/api/connectivity/lora';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/context/auth-context';
import { toast } from '@/hooks/use-toast';
import { getApiErrorMessage } from '@/lib/api';
import { getBatchResultToast } from '@/utils/lora-batch-result';

// Form schema
const batchActiveSchema = z.object({
  type: z.enum(['activate', 'deactivate']),
  payload_type: z.enum(['manual', 'csv', 'xlsx']),
  serials: z
    .array(
      z.object({
        serial: z.string(),
        from_file: z.string(),
      }),
    )
    .min(1, 'Adicione pelo menos um serial'),
  files: z
    .array(
      z.object({
        ext: z.enum(['xlsx', 'csv']),
        file_name: z.string(),
        serial_count: z.number(),
      }),
    )
    .optional(),
  manualInput: z.string().optional(),
});

type BatchActiveFormData = z.infer<typeof batchActiveSchema>;

interface SerialItem {
  serial: string;
  from_file: string;
}

interface FileItem {
  ext: 'csv' | 'xlsx';
  file_name: string;
  serial_count: number;
}

interface ApiResponseItem {
  status: boolean;
  serial: string;
  reason?: string;
}

// Sub-component: Manual Input Form
function ManualInputForm() {
  const form = useFormContext<BatchActiveFormData>();
  const payloadType = useWatch({ control: form.control, name: 'payload_type' });

  if (payloadType !== 'manual') return null;

  const handleManualInputChange = (value: string) => {
    const parts = value.split(';');
    const incompleteSerial = parts.at(-1) ?? '';
    const completedSerials = parts
      .slice(0, -1)
      .map((s) => s.trim())
      .filter(Boolean);

    const existingSerials = form.getValues('serials') ?? [];
    const newSerials = completedSerials.map((serial) => ({
      serial,
      from_file: 'Inserido manualmente',
    }));

    const otherSourceSerials = existingSerials.filter(
      (s) => s.from_file !== 'Inserido manualmente',
    );

    const merged = [...otherSourceSerials, ...newSerials];
    const seen = new Set<string>();
    const uniqueSerials = merged.filter((item) => {
      if (seen.has(item.serial)) return false;
      seen.add(item.serial);
      return true;
    });

    form.setValue('serials', uniqueSerials);
    form.setValue('manualInput', incompleteSerial);
  };

  return (
    <div className="space-y-1">
      <Controller
        name="manualInput"
        render={() => (
          <>
            <Label htmlFor="manual-serials">
              Digite os seriais separados por ponto e vírgula (;)
            </Label>
            <Textarea
              id="manual-serials"
              placeholder="00010002;00010003;00010004"
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                handleManualInputChange(e.target.value)
              }
              className="min-h-[120px] resize-none"
            />
            <p className="text-sm text-muted-foreground">Exemplo: 00010002;00010003;00010004</p>
          </>
        )}
      />
    </div>
  );
}

// Sub-component: CSV Input Form
function CsvInputForm() {
  const form = useFormContext<BatchActiveFormData>();
  const payloadType = useWatch({ control: form.control, name: 'payload_type' });
  const files = useWatch({ control: form.control, name: 'files', defaultValue: [] });
  const [isUploading, setIsUploading] = useState(false);

  if (payloadType !== 'csv') return null;

  const csvFiles = files?.filter((f) => f.ext === 'csv') ?? [];

  const handleFileUpload = async (file: File) => {
    const existingFiles = form.getValues('files') ?? [];
    if (existingFiles.some((f) => f.file_name === file.name)) {
      toast({
        title: 'Erro ao enviar arquivo',
        description: 'Esse arquivo já foi enviado',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter((line) => line.trim());
      const extractedSerials = lines.flatMap((line) =>
        line
          .split(/[;,]/)
          .map((serial) => serial.trim())
          .filter(Boolean),
      );

      const serials = form.getValues('serials') ?? [];
      const newSerials = extractedSerials.map((serial) => ({
        serial,
        from_file: file.name,
      }));

      form.setValue('serials', [...serials, ...newSerials]);
      form.setValue('files', [
        ...existingFiles,
        { ext: 'csv', file_name: file.name, serial_count: extractedSerials.length },
      ]);
    } catch (_error) {
      toast({
        title: 'Erro ao ler arquivo',
        description: 'Não foi possível processar o arquivo CSV',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteFile = (fileName: string) => {
    const serials = form.getValues('serials').filter((s) => s.from_file !== fileName);
    const newFiles = (form.getValues('files') ?? []).filter((f) => f.file_name !== fileName);
    form.setValue('serials', serials);
    form.setValue('files', newFiles);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Upload de arquivo CSV</Label>
        <div className="flex items-center justify-center h-72 border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-border/90 transition-colors">
          <div className="space-y-4">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <div>
              <Input
                id="csv-upload"
                type="file"
                accept=".csv"
                disabled={isUploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileUpload(file);
                    e.target.value = '';
                  }
                }}
                className="hidden"
              />
              <div className="space-y-1">
                <Label
                  htmlFor="csv-upload"
                  className="cursor-pointer text-sm inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
                >
                  {isUploading ? (
                    <>
                      <Loader className="animate-spin" size={12} />
                      Lendo arquivo enviado...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Adicionar arquivo CSV
                    </>
                  )}
                </Label>
                <p className="text-sm text-muted-foreground">ou arraste e solte o arquivo aqui</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {csvFiles.length > 0 && <ImportedFilesTable data={csvFiles} onDelete={handleDeleteFile} />}

      <Accordion type="multiple">
        <AccordionItem value="help" className="!rounded-xl !bg-blue-50 !border-blue-200">
          <AccordionTrigger className="p-3 !text-blue-800">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Formato esperado do arquivo CSV
            </div>
          </AccordionTrigger>
          <AccordionContent className="p-3 space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-blue-700 font-medium">Opção 1: Uma coluna com seriais</p>
              <div className="bg-white p-3 rounded border font-mono text-sm">
                00010002
                <br />
                00010003
                <br />
                00010004
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-blue-700 font-medium">
                Opção 2: Seriais separados por vírgula ou ponto e vírgula
              </p>
              <div className="bg-white p-3 rounded border font-mono text-sm">
                00010002,00010003,00010004
                <br />
                ou
                <br />
                00010002;00010003;00010004
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

// Sub-component: XLSX Input Form
function XlsxInputForm() {
  const form = useFormContext<BatchActiveFormData>();
  const payloadType = useWatch({ control: form.control, name: 'payload_type' });
  const files = useWatch({ control: form.control, name: 'files', defaultValue: [] });
  const [isUploading, setIsUploading] = useState(false);

  if (payloadType !== 'xlsx') return null;

  const xlsxFiles = files?.filter((f) => f.ext === 'xlsx') ?? [];

  const handleFileUpload = async (file: File) => {
    const existingFiles = form.getValues('files') ?? [];
    if (existingFiles.some((f) => f.file_name === file.name)) {
      toast({
        title: 'Erro ao enviar arquivo',
        description: 'Esse arquivo já foi enviado',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json<{ Serial?: string }>(worksheet);

      const extractedSerials = jsonData
        .map((row) => String(row.Serial || Object.values(row)[0] || '').trim())
        .filter(Boolean);

      const serials = form.getValues('serials') ?? [];
      const newSerials = extractedSerials.map((serial) => ({
        serial,
        from_file: file.name,
      }));

      form.setValue('serials', [...serials, ...newSerials]);
      form.setValue('files', [
        ...existingFiles,
        { ext: 'xlsx', file_name: file.name, serial_count: extractedSerials.length },
      ]);
    } catch (_error) {
      toast({
        title: 'Erro ao enviar arquivo',
        description: 'Certifique-se de estar utilizando uma planilha com o formato correto',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteFile = (fileName: string) => {
    const serials = form.getValues('serials').filter((s) => s.from_file !== fileName);
    const newFiles = (form.getValues('files') ?? []).filter((f) => f.file_name !== fileName);
    form.setValue('serials', serials);
    form.setValue('files', newFiles);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Upload de arquivo Excel</Label>
        <div className="flex items-center justify-center border-2 border-dashed border-border rounded-lg h-72 text-center hover:border-border/90 transition-colors">
          <div className="space-y-4">
            <div className="mx-auto w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
              <Upload className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <Input
                id="xlsx-upload"
                type="file"
                accept=".xlsx,.xls"
                disabled={isUploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileUpload(file);
                    e.target.value = '';
                  }
                }}
                className="hidden"
              />
              <Label
                htmlFor="xlsx-upload"
                className="cursor-pointer inline-flex text-sm items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
              >
                {isUploading ? (
                  <>
                    <Loader className="animate-spin" size={12} />
                    Lendo arquivo enviado...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Selecionar arquivo Excel
                  </>
                )}
              </Label>
            </div>
            <p className="text-sm text-gray-500">ou arraste e solte o arquivo aqui</p>
            <p className="text-xs text-gray-400">Formatos suportados: .xlsx, .xls</p>
          </div>
        </div>
      </div>

      {xlsxFiles.length > 0 && <ImportedFilesTable data={xlsxFiles} onDelete={handleDeleteFile} />}

      <Accordion type="multiple">
        <AccordionItem value="help" className="!rounded-xl !bg-green-50 !border-green-200">
          <AccordionTrigger className="p-3 text-green-700">
            <div className="flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Formato esperado da planilha Excel
            </div>
          </AccordionTrigger>
          <AccordionContent className="p-3 space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-green-700 font-medium">Estrutura recomendada:</p>
              <div className="bg-white rounded border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left border-r font-medium">A</th>
                      <th className="px-3 py-2 text-left font-medium">B</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    <tr className="border-t">
                      <td className="px-3 py-2 border-r">Serial</td>
                      <td className="px-3 py-2 text-gray-500">(opcional)</td>
                    </tr>
                    <tr className="border-t">
                      <td className="px-3 py-2 border-r">00010002</td>
                      <td className="px-3 py-2 text-gray-500">Descrição</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-sm text-green-700">
              <strong>Importante:</strong> Os seriais devem estar na primeira coluna (coluna A).
            </p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

// Sub-component: Imported Files Table
function ImportedFilesTable({
  data,
  onDelete,
}: {
  data: FileItem[];
  onDelete: (fileName: string) => void;
}) {
  const columns: ColumnDef<FileItem>[] = [
    { header: 'Arquivo', accessorKey: 'file_name' },
    { header: 'Qnt. de seriais', accessorKey: 'serial_count' },
    {
      id: 'action',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button
            onClick={() => onDelete(row.original.file_name)}
            variant="outline"
            className="!w-6 !h-6 !p-0"
            type="button"
          >
            <Trash size={14} />
          </Button>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="rounded-md border w-full bg-background shadow-sm">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// Sub-component: Serials Display
function SerialsDisplay() {
  const form = useFormContext<BatchActiveFormData>();
  const serials = useWatch({ name: 'serials', control: form.control, defaultValue: [] });

  const handleDelete = (serial: string) => {
    const newSerials = form.getValues('serials').filter((s) => s.serial !== serial);
    form.setValue('serials', newSerials);
  };

  const columns: ColumnDef<SerialItem>[] = [
    { header: 'Serial', accessorKey: 'serial' },
    { header: 'Método e arquivo', accessorKey: 'from_file' },
    {
      id: 'action',
      header: '',
      cell: ({ row }) => {
        if (row.original.from_file === 'Inserido manualmente') {
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" type="button" className="!w-6 !h-6 !p-0">
                    <HelpCircle size={14} className="text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="w-60">
                  <span className="font-medium">Inserido manualmente</span>
                  <br />
                  Remova o serial através do campo de texto no tipo de envio manual
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }
        return (
          <div className="flex justify-end">
            <Button
              variant="outline"
              className="!w-6 !h-6 !p-0"
              type="button"
              onClick={() => handleDelete(row.original.serial)}
            >
              <Trash size={14} />
            </Button>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: serials,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!serials || serials.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 border border-border rounded-md">
        <h3 className="text-lg font-medium">Pré-visualizar seriais</h3>
        <p className="text-sm text-muted-foreground">
          Preencha os seriais acima para gerenciá-los aqui
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border w-full">
      <TooltipProvider>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TooltipProvider>
    </div>
  );
}

// Sub-component: API Response Display
function ApiResponseDisplay({ data }: { data: ApiResponseItem[] }) {
  const columns: ColumnDef<ApiResponseItem>[] = [
    {
      header: '#',
      cell: ({ row }) => {
        if (row.original.status) {
          return <Badge className="bg-green-500">SUCESSO</Badge>;
        }
        return <Badge variant="destructive">ERRO</Badge>;
      },
    },
    { header: 'Serial', accessorKey: 'serial' },
    {
      header: '',
      accessorKey: 'reason',
      cell: ({ row }) => row.original.reason ?? 'Enviado pra fila de processamento',
    },
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 border border-border rounded-md">
        <h3 className="text-lg font-medium">Logs de resposta</h3>
        <p className="text-sm text-muted-foreground">
          Envie o formulário pra visualizar quais seriais foram enviados para o processamento e
          quais falharam
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border w-full">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// Main Page Component
export function BatchLoraActivationPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<'preview' | 'api-response'>('preview');
  const [apiResponse, setApiResponse] = useState<ApiResponseItem[]>([]);

  const form = useForm<BatchActiveFormData>({
    resolver: zodResolver(batchActiveSchema),
    defaultValues: {
      type: 'activate',
      payload_type: 'manual',
      serials: [],
      files: [],
    },
  });

  const batchActivateMutation = useMutation({
    mutationFn: async (data: BatchActiveFormData) => {
      if (!user) throw new Error('Usuário não autenticado');

      const serialsByFile = data.serials.reduce(
        (acc, item) => {
          const fileKey = item.from_file || 'manual';
          if (!acc[fileKey]) acc[fileKey] = [];
          acc[fileKey].push(item.serial);
          return acc;
        },
        {} as Record<string, string[]>,
      );

      const batch_metadata = Object.entries(serialsByFile).map(([fromFile, serials]) => ({
        serials,
        type: data.payload_type,
        file_name:
          fromFile !== 'manual' && fromFile !== 'Inserido manualmente' ? fromFile : undefined,
      }));

      return loraApi.batchActivate({
        type: data.type,
        batch_metadata,
        author: {
          user_uuid: user.id,
          name: user.name,
        },
      });
    },
    onSuccess: (data, variables) => {
      toast(getBatchResultToast(data.results, variables.type));

      const rejected = data.results.rejected.map((r) => ({
        ...r,
        status: false,
      }));
      const fulfilled = data.results.fulfilled.map((f) => ({
        ...f,
        status: true,
      }));

      setApiResponse([...rejected, ...fulfilled]);
      setTab('api-response');
    },
    onError: (error: Error, variables) => {
      toast({
        title: `Não foi possível requisitar a ${variables.type === 'activate' ? 'ativação' : 'desativação'} desses seriais`,
        description: getApiErrorMessage(error, error.message),
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = form.handleSubmit(
    (data) => batchActivateMutation.mutate(data),
    (errors) => {
      console.log('Form errors:', errors);
    },
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex-1 space-y-6 p-6 py-10 overflow-y-auto h-full"
    >
      <div className="w-[50vw] space-y-6">
        <FormProvider {...form}>
          <CardHeader className="px-0 pt-0 pb-0">
            <CardTitle>Ativação/desativação em massa</CardTitle>
            <CardDescription>
              Preencha o formulário pra fazer uma requisição de processamento que irá ativar ou
              desativar múltiplas chaves LoRa a partir dos seriais dos dispositivos registrados.
              Acompanhe o andamento do processamento nos{' '}
              <Link to="/connectivity/lora-activation-logs" className="underline" target="_blank">
                logs de gerenciamento das chaves LoRa.
              </Link>
            </CardDescription>
          </CardHeader>

          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>Ação e tipo de envio</CardTitle>
              <CardDescription>
                Escolha o que acontecerá com todos os seriais e como você irá selecionar os serials
                desejados
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Controller
                control={form.control}
                name="type"
                render={({ field }) => (
                  <div className="space-y-1">
                    <Label>Selecione a ação desejada</Label>
                    <RadioGroup
                      value={field.value}
                      className="grid group grid-cols-2 gap-2"
                      onValueChange={field.onChange}
                    >
                      <Label
                        htmlFor="activate"
                        className="cursor-pointer font-normal flex gap-2 hover:bg-accent transition-all items-center space-x-2 border rounded-md p-3"
                      >
                        <RadioGroupItem value="activate" id="activate" />
                        Ativar seriais
                      </Label>
                      <Label
                        htmlFor="deactivate"
                        className="cursor-pointer font-normal flex gap-2 hover:bg-accent transition-all items-center space-x-2 border rounded-md p-3"
                      >
                        <RadioGroupItem value="deactivate" id="deactivate" />
                        Desativar seriais
                      </Label>
                    </RadioGroup>
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="payload_type"
                render={({ field }) => (
                  <div className="space-y-1">
                    <Label>Tipo de envio</Label>
                    <RadioGroup
                      value={field.value}
                      className="grid group grid-cols-3 gap-2"
                      onValueChange={field.onChange}
                    >
                      <Label
                        htmlFor="manual"
                        className="cursor-pointer font-normal flex gap-2 hover:bg-accent transition-all items-center space-x-2 border rounded-md p-3"
                      >
                        <RadioGroupItem value="manual" id="manual" />
                        <div className="flex items-center gap-2">
                          <Edit3 className="w-4 h-4" />
                          <span>Manual</span>
                        </div>
                      </Label>
                      <Label
                        htmlFor="csv"
                        className="cursor-pointer font-normal flex gap-2 hover:bg-accent transition-all items-center space-x-2 border rounded-md p-3"
                      >
                        <RadioGroupItem value="csv" id="csv" />
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          <span>CSV</span>
                        </div>
                      </Label>
                      <Label
                        htmlFor="xlsx"
                        className="cursor-pointer font-normal flex gap-2 hover:bg-accent transition-all items-center space-x-2 border rounded-md p-3"
                      >
                        <RadioGroupItem value="xlsx" id="xlsx" />
                        <div className="flex items-center gap-2">
                          <Upload className="w-4 h-4" />
                          <span>Planilha Excel</span>
                        </div>
                      </Label>
                    </RadioGroup>
                  </div>
                )}
              />
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>Preenchimento dos dados</CardTitle>
              <CardDescription>
                Adicione os seriais desejados. O serial deve pertencer a um dispositivo registrado
                no BCube e no KORA.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ManualInputForm />
              <CsvInputForm />
              <XlsxInputForm />
            </CardContent>
          </Card>

          <Tabs value={tab} onValueChange={(v: string) => setTab(v as 'preview' | 'api-response')}>
            <TabsList>
              <TabsTrigger value="preview">Gerenciar seriais</TabsTrigger>
              <TabsTrigger value="api-response">Logs de resposta</TabsTrigger>
            </TabsList>
            <TabsContent value="preview">
              <SerialsDisplay />
            </TabsContent>
            <TabsContent value="api-response">
              {apiResponse.length !== 0 && (
                <Alert variant="info" className="mb-2">
                  <ExternalLink size={14} />
                  <AlertDescription className="mt-1">
                    Acompanhe o andamento do processamento dos dispositivos enviados com sucesso no
                    <Link
                      to="/connectivity/lora-activation-logs"
                      className="underline ml-1"
                      target="_blank"
                    >
                      logs de gerenciamento das chaves LoRa
                    </Link>
                  </AlertDescription>
                </Alert>
              )}
              <ApiResponseDisplay data={apiResponse} />
            </TabsContent>
          </Tabs>

          <div>
            <Button
              onClick={handleSubmit}
              disabled={form.formState.isSubmitting || batchActivateMutation.isPending}
              className="bg-black text-white hover:bg-gray-800"
            >
              {(form.formState.isSubmitting || batchActivateMutation.isPending) && (
                <Loader className="animate-spin" size={12} />
              )}
              {form.formState.isSubmitting || batchActivateMutation.isPending
                ? 'Enviando...'
                : 'Enviar'}
            </Button>
          </div>
        </FormProvider>
      </div>
    </motion.div>
  );
}
